<template>
  <b-modal
    :active.sync="opened"
    width="450px"
    scroll="keep"
    @close="resetEventDetails()"
  >
    <div class="card">
      <div class="card-content tw-p-6">
        <div class="section-block">
          <span class="title">Event Details</span>
        </div>

        <skeleton-loading v-if="isLoading" />
        <div v-else class="columns">
          <div class="column event-details">
            <h1 :style="`color: ${eventColor}`">
              {{ eventDetails.title }}
            </h1>
            <ul class="event-types" v-if="eventCat">
              <li class="tag" :style="`background-color: ${eventColor}`">
                {{ eventCat }}
              </li>
            </ul>
            <p
              style="white-space: pre-wrap;"
              v-if="extProps.description"
              v-html="eventDescription"
            ></p>
            <hr />
            <p>
              <strong>Date:</strong>
              {{ formatDate(date.start, 'MMMM D') }}
              <span v-if="date.end && isRecurring">
                - {{ formatDate(date.end, 'MMMM D') }}</span
              >
            </p>
            <p v-if="!isAllDay">
              <strong>Time:</strong>
              {{ formatTime(time.start, 'LT') }}
              <span v-if="time.end"> - {{ formatTime(time.end, 'LT') }} </span>
            </p>

            <p v-if="isRecurring">
              <strong>Every: </strong>
              <span v-if="extProps.repeat_by === `daily`">Daily</span>
              <span
                v-else
                v-for="(repeat, i) in recurring.repeats"
                :key="`repeat-` + i"
                >&nbsp;{{ repeat
                }}<span v-if="i <= recurring.repeats.length - 2">,</span>
              </span>
            </p>

            <div class="level tw-mt-10" v-if="authEdit">
              <div class="level-left"></div>
              <div class="level-right">
                <b-button type="is-primary" @click.prevent="openEventForm()"
                  >Edit Event</b-button
                >
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </b-modal>
</template>

<script>
import linkifyStr from 'linkifyjs/string'
import { mapState, mapActions } from 'vuex'
import ModalMixin from '@/utils/mixins/modal'
import dayjs from 'dayjs'

export default {
  name: 'ViewCalendarEvent',
  mixins: [ModalMixin],
  props: {
    event: { type: Object }
  },
  data() {
    return {
      opened: false,
      isAllDay: false,
      isRecurring: false,
      isLoading: false,
      date: {
        start: '',
        end: ''
      },
      time: {
        start: '',
        end: ''
      },
      recurring: {
        weekdays: [],
        repeats: []
      },
      extProps: {},
      eventDetails: {}
    }
  },
  computed: {
    ...mapState({
      calendarTz: state => state.calendar.options.timeZone,
      group: state => state.group.currentGroup
    }),
    authEdit() {
      return !!(
        this.group &&
        this.group.is_group_leader &&
        this.eventDetails &&
        this.eventDetails.extendedProps &&
        this.eventDetails.extendedProps.event_type === 'group'
      )
    },
    eventColor() {
      return this.eventDetails &&
        this.eventDetails.extendedProps &&
        this.eventDetails.extendedProps.event_type === 'group'
        ? this.eventDetails.color
        : '#00adcd'
    },
    eventCat() {
      return this.eventDetails &&
        this.eventDetails.extendedProps &&
        this.eventDetails.extendedProps.event_type === 'master'
        ? 'Mustard Event'
        : this.eventDetails.extendedProps
        ? this.eventDetails.extendedProps.category
        : ''
    },
    eventDescription() {
      return linkifyStr(this.extProps.description, {
        defaultProtocol: 'https',
        ignoreTags: ['script', 'style']
      })
    }
  },
  methods: {
    ...mapActions({
      getCalendarMasterSingleEvent: 'getCalendarMasterSingleEvent',
      getCalendarSingleEvent: 'group/getCalendarSingleEvent'
    }),

    async open() {
      this.opened = true
      this.recurring.weekdays = dayjs.weekdays()
      await this.getEvent()
    },
    getAllEventDetails() {
      this.isAllDay = !!this.eventDetails.allDay

      if (this.eventDetails.startRecur) {
        this.isRecurring = true
        this.date.start = this.eventDetails.startRecur
        this.date.end = this.eventDetails.endRecur
          ? this.eventDetails.endRecur
          : ''
        this.time.start = this.eventDetails.startRecur
        this.time.end = this.eventDetails.endRecur

        this.recurring.repeats = []

        this.eventDetails.daysOfWeek.sort((a, b) => a - b)
        this.eventDetails.daysOfWeek.forEach(day => {
          this.recurring.repeats.push(this.recurring.weekdays[day])
        })
      } else {
        this.date.start = this.eventDetails.start
        this.date.end = this.eventDetails.end ? this.eventDetails.end : ''
        this.time.start = this.eventDetails.start
        this.time.end = this.eventDetails.end
      }

      this.extProps = this.eventDetails.extendedProps
    },
    async getEvent() {
      this.isLoading = true

      this.event.type === 'master'
        ? this.getMasterEvent()
        : this.getGroupEvent()
    },
    async getMasterEvent() {
      await this.getCalendarMasterSingleEvent(this.event.id)
        .then(data => {
          this.eventDetails = data
          this.getAllEventDetails()
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    async getGroupEvent() {
      await this.getCalendarSingleEvent(this.event.id)
        .then(data => {
          this.eventDetails = data
          this.getAllEventDetails()
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    formatTime(time, format) {
      return dayjs(time)
        .tz(this.calendarTz)
        .format(format)
    },
    formatDate(date, format) {
      return dayjs(date)
        .tz(this.calendarTz)
        .format(format)
    },
    resetEventDetails() {
      this.date = {}
      this.time = {}
      this.eventDetails = {}
      this.extProps = {}
      this.isRecurring = this.isAllDay = false
      this.recurring.repeats = []
      this.$emit('closed')
    },
    async openEventForm() {
      this.opened = false
      await this.$emit('openEventForm')
    }
  }
}
</script>

<style lang="sass" scoped>
h1
    font-size: 1.2em
    line-height: 1em
    font-weight: bold
    margin-bottom: 0.5em

p
    margin: 10px 0
    line-height: 1.3em

.event-details
  letter-spacing: 0 !important
  word-break: break-word

.event-types
    line-height: 1em

    .tag
        color: #fff
        font-weight: bold
        line-height: 2em
        display: inline-block
        text-transform: capitalize

.section-block
    margin: 0 0 15px

    .title
        padding: 0 0 5px
</style>
